/**
 * Editor for customizable organizer type templates.
 * Allows viewing, seeding, adding, editing, and removing sections and fields.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../api';

const OrganizerTemplateEditor = ({ open, onClose, organizerType: initialType, organizerTypes = [] }) => {
  const [organizerType, setOrganizerType] = useState(initialType);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => { setOrganizerType(initialType); }, [initialType]);
  useEffect(() => {
    if (open && organizerType?.id) fetchTemplates();
  }, [open, organizerType?.id]);

  const fetchTemplates = async () => {
    if (!organizerType?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/organizer-types/${organizerType.id}/templates/`);
      if (res.data?.success) setSections(res.data.data?.sections || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    if (!organizerType?.id) return;
    setSeeding(true);
    setError(null);
    try {
      const res = await api.post(`/api/organizer-types/${organizerType.id}/templates/`);
      if (res.data?.success) {
        fetchTemplates();
      } else {
        setError(res.data?.error || 'Seed failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to seed templates');
    } finally {
      setSeeding(false);
    }
  };

  const handleAddSection = async () => {
    if (!organizerType?.id) return;
    try {
      await api.post(`/api/organizer-types/${organizerType.id}/templates/sections/`, {
        section_type: 'additional_info',
        title: 'New Section',
        order: sections.length + 1,
      });
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add section');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!organizerType?.id || !window.confirm('Delete this section and all its fields?')) return;
    try {
      await api.delete(`/api/organizer-types/${organizerType.id}/templates/sections/${sectionId}/`);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete section');
    }
  };

  const handleAddField = async (sectionId) => {
    if (!organizerType?.id) return;
    try {
      await api.post(`/api/organizer-types/${organizerType.id}/templates/sections/${sectionId}/fields/`, {
        field_name: `field_${Date.now()}`,
        field_type: 'text',
        label: 'New Field',
        order: 99,
      });
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add field');
    }
  };

  const handleDeleteField = async (sectionId, fieldId) => {
    if (!organizerType?.id || !window.confirm('Delete this field?')) return;
    try {
      await api.delete(`/api/organizer-types/${organizerType.id}/templates/sections/${sectionId}/fields/${fieldId}/`);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete field');
    }
  };

  const handleClose = () => {
    setOrganizerType(null);
    onClose();
  };

  if (!open) return null;

  if (!organizerType && organizerTypes.length > 0) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Customize Templates</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" gutterBottom>Select organizer type to customize</Typography>
          <Box display="flex" flexDirection="column" gap={1} pt={2}>
            {organizerTypes.map((t) => (
              <Button key={t.id} variant="outlined" onClick={() => setOrganizerType(t)}>
                {t.name}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!organizerType) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Customize Templates: {organizerType.name}
        <Button size="small" sx={{ ml: 2 }} onClick={() => setOrganizerType(null)}>Change Type</Button>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : sections.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary" gutterBottom>
              No custom templates yet. Seed from default to get started.
            </Typography>
            <Button
              variant="contained"
              onClick={handleSeed}
              disabled={seeding}
              startIcon={seeding ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {seeding ? 'Seeding...' : 'Seed from Default'}
            </Button>
          </Box>
        ) : (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2">Sections and Fields</Typography>
              <Box>
                <Button size="small" onClick={handleSeed} disabled={seeding} sx={{ mr: 1 }}>
                  Re-seed (replaces all)
                </Button>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddSection}>
                  Add Section
                </Button>
              </Box>
            </Box>
            {sections.map((sec) => (
              <Accordion key={sec.id} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>{sec.title}</Typography>
                  <Chip label={sec.section_type} size="small" sx={{ ml: 2 }} />
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">{sec.fields?.length || 0} fields</Typography>
                    <Box>
                      <IconButton size="small" onClick={() => handleAddField(sec.id)} title="Add field">
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteSection(sec.id)} title="Delete section" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <List dense>
                    {(sec.fields || []).map((f) => (
                      <ListItem key={f.id} sx={{ py: 0.5 }}>
                        <ListItemText primary={f.label} secondary={`${f.field_type}${f.is_required ? ' (required)' : ''}`} />
                        <IconButton size="small" onClick={() => handleDeleteField(sec.id, f.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizerTemplateEditor;
